import { crc8 } from '../crc8'
import {
  decrypt,
  encrypt,
  generateEncryptKey,
  generateFourRandomValues,
} from './aesCrypto'

export function createLegacyCommand(cmd: number): number[] {
  const withoutCrc = [cmd, 0x03]
  return [...withoutCrc, crc8(withoutCrc)]
}

export function createModernCommand(cmd: number, isRental: boolean): number[] {
  const withoutCrc = [
    cmd,
    9,
    0x02,
    0x01,
    0x00,
    isRental ? 0x0e : 0x0f,
    isRental ? 0x88 : 0xd6,
    0xff,
  ]
  return [...withoutCrc, crc8(withoutCrc)]
}

export function buildAesPacket(
  cmd: number,
  time: number,
  opts: { isLegacyFirmware?: boolean; isRental?: boolean } = {}
): Uint8Array {
  const { isLegacyFirmware = true, isRental = false } = opts
  const randoms = generateFourRandomValues(time)
  const key = generateEncryptKey(randoms)
  const command = isLegacyFirmware
    ? createLegacyCommand(cmd)
    : createModernCommand(cmd, isRental)
  const encrypted = encrypt(new Uint8Array(command), key, new Uint8Array(16))
  return frame(randoms, encrypted)
}

function frame(randoms: Uint8Array, encrypted: Uint8Array): Uint8Array {
  const header = 0xa5
  const footer = 0xa5
  const packetLength = encrypted.length + 8
  const withoutCrc = [header, packetLength, ...randoms, ...encrypted]
  const crc = crc8(withoutCrc)
  return Uint8Array.from([...withoutCrc, crc, footer])
}

export function readAesResponse(packet: Uint8Array): number[] {
  const randoms = packet.slice(2, 6)
  const encrypted = packet.slice(6, -2)
  const key = generateEncryptKey(randoms)

  // 16 byte チャンクに分割して各々独立 decrypt（IV 全0・CBC chaining しない＝platform 同一）。
  // NOTE: platform `handleResponse` は responseData[1]==40(0x28) の時のみ2ブロック分割し、それ以外は一括 decrypt する。
  // ここでは常に16byteチャンク分割するが、暗号文長は必ず16の倍数（16/32）なので有効入力では等価。
  // NOTE: 不正長フレームの length 検証（silent 誤デコード防止・invalid frame は drop）は消費側の
  // isWellFormedAesFrame ゲートで実施済。live decode 経路へ本格昇格する際の (1) 実機16byte応答での
  // handleResponse responseData[1]==40 分岐との等価性再確認、(2) frame crc 検証 は TODO(aikasa/ikasa_platform#7595)。
  const chunks: Uint8Array[] = []
  encrypted.forEach((b, idx) => {
    if (idx % 16 === 0) chunks.push(new Uint8Array(16))
    chunks[chunks.length - 1][idx % 16] = b
  })

  const decrypted: number[] = []
  for (const chunk of chunks) {
    decrypted.push(...decrypt(chunk, key, new Uint8Array(16)))
  }
  return decrypted
}
